<template>
  <v2-card>
    <template #header>
      <h3 class="v2-heading-sm">
        Segmentation
      </h3>
    </template>

    <div class="v2-seg__grid">
      <!-- Minimum Order Value -->
      <div class="v2-seg__field">
        <v2-input
          v-model="segmentation.segmentationLocal.value.minimumOrderValue"
          :label="'Minimum Order Value (' + currency + ')'"
          type="number"
          placeholder="Minimum Order Value"
          :disabled="segmentation.isNotEntityAdmin.value"
        />
      </div>

      <!-- Exclude Countries -->
      <div class="v2-seg__field">
        <div class="v2-seg__country-header">
          <label class="v2-seg__label">Exclude Countries</label>
          <div class="v2-seg__country-controls">
            <v2-checkbox
              class="v2-seg__select-all-checkbox"
              :value="segmentation.selectAllCountries.value"
              :label="segmentation.selectAllCountries.value ? 'Deselect All' : 'Select All'"
              :disabled="segmentation.isNotEntityAdmin.value"
              @input="onSelectAllChange"
            />
            <v2-badge variant="primary">
              {{ restrictedCount }} Selected
            </v2-badge>
          </div>
        </div>
        <v2-multi-select
          :value="segmentation.segmentationLocal.value.restrictedCountries"
          :option-groups="segmentation.countryOptionGroups.value"
          placeholder="Select Country"
          :searchable="true"
          :disabled="segmentation.isNotEntityAdmin.value"
          no-data-text="No country found."
          @input="onCountryInput"
          @search="segmentation.setCountries"
        />
      </div>
    </div>

    <!-- Actions -->
    <div class="v2-seg__actions">
      <v2-button
        variant="primary"
        :loading="segmentation.loading.value"
        :disabled="segmentation.loading.value || segmentation.isNotEntityAdmin.value"
        @click="onSave"
      >
        Save changes
      </v2-button>
      <v2-button
        variant="outline"
        :disabled="segmentation.isNotEntityAdmin.value"
        @click="onReset"
      >
        Reset
      </v2-button>
    </div>
  </v2-card>
</template>

<script>
import { computed } from '@vue/composition-api'
import V2Card from '@/components/v2/V2Card.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2MultiSelect from '@/components/v2/V2MultiSelect.vue'

export default {
  name: 'V2SegmentationSection',
  components: {
    V2Card,
    V2Input,
    V2Button,
    V2Checkbox,
    V2Badge,
    V2MultiSelect,
  },
  props: {
    segmentation: {
      type: Object,
      required: true,
    },
    segmentationData: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const currency = computed(() => props.segmentation.profileData.value?.currency || 'USD')
    const restrictedCount = computed(() => props.segmentation.segmentationLocal.value?.restrictedCountries?.length || 0)

    function onSelectAllChange(val) {
      props.segmentation.selectAllCountries.value = val
      props.segmentation.handleSelectAllCountries()
    }

    function onCountryInput(countries) {
      props.segmentation.handleSelectCountry(countries)
    }

    function onSave() {
      props.segmentation.saveSegmentation()
    }

    function onReset() {
      props.segmentation.resetForm(props.segmentationData)
    }

    return {
      currency,
      restrictedCount,
      onSelectAllChange,
      onCountryInput,
      onSave,
      onReset,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-seg {
  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-6);
    margin-top: var(--v2-space-4);
  }

  &__field {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__country-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--v2-space-1);
  }

  &__country-controls {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
  }

  &__select-all-checkbox {
    margin-bottom: 0;
  }

  &__actions {
    display: flex;
    gap: var(--v2-space-3);
    margin-top: var(--v2-space-5);
  }
}
</style>
